import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, XCircle, Loader2, Pencil, Keyboard } from "lucide-react";

const CURSIVE = { fontFamily: "'Dancing Script', cursive" };

function costText(d) {
  if (d.cost_impact === "increase") return `+$${d.cost_amount ?? ""}`;
  if (d.cost_impact === "decrease") return `-$${d.cost_amount ?? ""}`;
  if (d.cost_impact === "no_change") return "No change";
  return "TBC";
}
function timeText(d) {
  if (d.timeline_impact === "extension") return `+${d.timeline_days ?? ""} days`;
  if (d.timeline_impact === "reduction") return `-${d.timeline_days ?? ""} days`;
  if (d.timeline_impact === "no_change") return "No change";
  return "TBC";
}

export default function ClientVariationPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [choice, setChoice] = useState(null); // "approved" | "rejected"
  const [note, setNote] = useState("");
  const [sigMode, setSigMode] = useState("draw"); // "draw" | "type"
  const [typedName, setTypedName] = useState("");
  const [drawnName, setDrawnName] = useState("");
  const [drawnData, setDrawnData] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);
  const sigSectionRef = useRef(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/public/client-variation/${token}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setState({ loading: false, error: e.detail || "This link is no longer active.", data: null });
        return;
      }
      setState({ loading: false, error: null, data: await res.json() });
    } catch {
      setState({ loading: false, error: "Could not load this record.", data: null });
    }
  };

  useEffect(() => {
    load();
  }, [token]); // eslint-disable-line

  // react-signature-canvas wipes on resize (mobile address bar) — capture + restore.
  const captureDrawn = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setDrawnData(sigRef.current.toDataURL("image/png"));
    }
  };
  useEffect(() => {
    const restore = () => {
      setTimeout(() => {
        if (drawnData && sigRef.current?.isEmpty()) {
          sigRef.current.fromDataURL(drawnData);
        }
      }, 150);
    };
    window.addEventListener("resize", restore);
    window.addEventListener("orientationchange", restore);
    return () => {
      window.removeEventListener("resize", restore);
      window.removeEventListener("orientationchange", restore);
    };
  }, [drawnData]);

  const clearDrawn = () => {
    sigRef.current?.clear();
    setDrawnData("");
  };

  const switchMode = (mode) => {
    if (mode === sigMode) return;
    // Switching tabs clears the other tab's data — only one signature mode submitted.
    if (mode === "type") {
      clearDrawn();
      setDrawnName("");
    } else {
      setTypedName("");
    }
    setSigMode(mode);
  };

  const onChoose = (val) => {
    setChoice(val);
    setTimeout(() => sigSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const sigValid =
    sigMode === "type"
      ? typedName.trim().length > 0
      : drawnData.length > 0 && drawnName.trim().length > 0;
  const canSubmit = !!choice && sigValid && !submitting;

  const submit = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    const payload =
      sigMode === "type"
        ? {
            response: choice,
            note,
            signature_type: "typed",
            signature_data: typedName.trim(),
            signature_name: typedName.trim(),
          }
        : {
            response: choice,
            note,
            signature_type: "drawn",
            signature_data: drawnData,
            signature_name: drawnName.trim(),
          };
    try {
      const res = await fetch(`${API}/public/client-variation/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await load();
      } else {
        const e = await res.json().catch(() => ({}));
        alert(e.detail || "Could not submit response");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-var-error">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-slate-800">Link unavailable</p>
          <p className="text-slate-500 mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const responded = !!d.client_response;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-var-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png?v=3" alt="SiteProApp" className="w-9 h-9 object-contain" />
          <div>
            <h1 className="font-bold text-slate-900">Variation for your review</h1>
            <p className="text-sm text-slate-500">{d.site_address || d.site_name}</p>
          </div>
        </div>

        {/* STEP 1 — variation card */}
        <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-slate-900">{d.variation_number}</span>
            <span className="text-sm text-slate-500">{d.date_raised}</span>
          </div>
          <div className="text-sm">
            <p className="text-slate-500">Raised by</p>
            <p className="font-medium text-slate-800 capitalize">{d.raised_by}</p>
          </div>
          <div className="text-sm">
            <p className="text-slate-500">Description</p>
            <p className="text-slate-800 whitespace-pre-wrap">{d.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div className="text-sm">
              <p className="text-slate-500">Cost impact</p>
              <p className="font-semibold text-slate-900">{costText(d)}</p>
            </div>
            <div className="text-sm">
              <p className="text-slate-500">Timeline impact</p>
              <p className="font-semibold text-slate-900">{timeText(d)}</p>
            </div>
          </div>
        </div>

        {responded ? (
          /* STEP 5 — confirmation screen */
          <div
            className={`mt-6 rounded-2xl p-6 border text-center ${
              d.client_response === "approved" ? "bg-green-50 border-green-200" : "bg-rose-50 border-rose-200"
            }`}
            data-testid="public-var-responded"
          >
            {d.client_response === "approved" ? (
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            ) : (
              <XCircle className="w-12 h-12 text-rose-600 mx-auto" />
            )}
            <p
              className={`text-2xl font-bold mt-2 ${
                d.client_response === "approved" ? "text-green-800" : "text-rose-800"
              }`}
            >
              {d.client_response === "approved" ? "Approved" : "Rejected"}
            </p>
            <p className="text-slate-600 mt-1">Your response has been recorded.</p>
            <p className="text-sm text-slate-500 mt-3">
              {d.variation_number} — {d.description?.slice(0, 80)}
            </p>
            {d.client_note && (
              <p className="text-sm text-slate-700 mt-2 italic">"{d.client_note}"</p>
            )}

            <div className="mt-4 bg-white rounded-xl border p-4">
              <p className="text-xs uppercase tracking-wide text-slate-400">Signed by</p>
              {d.signature_type === "drawn" && d.signature_data ? (
                <img
                  src={d.signature_data}
                  alt="signature"
                  className="h-16 mx-auto mt-1"
                  data-testid="public-var-sig-thumb"
                />
              ) : (
                <p className="text-3xl text-slate-800 mt-1" style={CURSIVE} data-testid="public-var-sig-typed">
                  {d.signature_name}
                </p>
              )}
              <p className="text-sm font-medium text-slate-700 mt-1">{d.signature_name}</p>
              <p className="text-xs text-slate-500">
                Signed {d.signed_at ? new Date(d.signed_at).toLocaleString("en-AU") : ""}
              </p>
            </div>
            <p className="text-sm text-slate-500 mt-4">Your builder has been notified.</p>
          </div>
        ) : (
          <>
            {/* STEP 2 — decision */}
            <div className="mt-6 bg-white rounded-2xl shadow-sm border p-5 space-y-4">
              <p className="text-sm font-medium text-slate-700">
                Your decision — you will be asked to sign below
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onChoose("approved")}
                  className={`h-12 rounded-xl font-semibold border-2 transition-colors ${
                    choice === "approved"
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white text-green-700 border-green-300"
                  }`}
                  data-testid="public-var-approve-btn"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => onChoose("rejected")}
                  className={`h-12 rounded-xl font-semibold border-2 transition-colors ${
                    choice === "rejected"
                      ? "bg-rose-600 text-white border-rose-600"
                      : "bg-white text-rose-700 border-rose-300"
                  }`}
                  data-testid="public-var-reject-btn"
                >
                  Reject
                </button>
              </div>
              <Textarea
                placeholder="Add a note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="public-var-note"
              />
            </div>

            {/* STEP 3 — signature */}
            <div
              ref={sigSectionRef}
              className="mt-6 bg-white rounded-2xl shadow-sm border p-5 space-y-4"
              data-testid="public-var-signature-section"
            >
              <p className="font-semibold text-slate-800">Sign to confirm</p>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => switchMode("draw")}
                  className={`flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-medium border transition-colors ${
                    sigMode === "draw"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-300"
                  }`}
                  data-testid="sig-tab-draw"
                >
                  <Pencil className="w-4 h-4" /> Draw Signature
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("type")}
                  className={`flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-medium border transition-colors ${
                    sigMode === "type"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-300"
                  }`}
                  data-testid="sig-tab-type"
                >
                  <Keyboard className="w-4 h-4" /> Type Name
                </button>
              </div>

              {sigMode === "draw" ? (
                <div className="space-y-2">
                  <div className="border-2 border-slate-300 rounded-lg bg-white touch-none">
                    <SignatureCanvas
                      ref={sigRef}
                      onEnd={captureDrawn}
                      penColor="#0f172a"
                      canvasProps={{
                        className: "w-full h-40 rounded-lg",
                        "data-testid": "sig-canvas",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearDrawn}
                    className="text-sm text-slate-500 underline"
                    data-testid="sig-clear-btn"
                  >
                    Clear signature
                  </button>
                  <p className="text-xs text-slate-500">
                    Draw your signature above using your finger or stylus.
                  </p>
                  <div>
                    <label className="text-sm text-slate-600">Full name *</label>
                    <Input
                      value={drawnName}
                      onChange={(e) => setDrawnName(e.target.value)}
                      placeholder="Type your full name"
                      data-testid="sig-drawn-name"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm text-slate-600">Type your full legal name *</label>
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Your full legal name"
                    className="text-center text-lg"
                    data-testid="sig-typed-input"
                  />
                  {typedName.trim() && (
                    <div className="text-center py-3 bg-slate-50 rounded-lg border">
                      <span className="text-4xl text-slate-800" style={CURSIVE} data-testid="sig-typed-preview">
                        {typedName}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    Your typed name serves as your legal signature for this variation.
                  </p>
                </div>
              )}

              {/* Legal declaration */}
              <p className="text-xs text-slate-500 border-t pt-3">
                By signing below, I confirm my decision regarding Variation {d.variation_number} for{" "}
                {d.site_address || d.site_name}. I understand this response is recorded with my name, the
                date and time, and my IP address, and constitutes my formal response to this variation.
              </p>

              {/* STEP 4 — submit */}
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={!canSubmit}
                className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600"
                data-testid="public-var-submit-btn"
              >
                {submitting ? "Submitting..." : "Submit Response"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Confirm modal */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {choice === "approved" ? "Approve" : "Reject"} {d.variation_number}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to {choice === "approved" ? "approve" : "reject"} this variation. This cannot be
              undone. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submit}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="public-var-confirm-submit-btn"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
